/**
 * Count todos for a Basecamp todolist, including nested section lists.
 * Parent lists often have zero top-level todos - work lives in sections.
 */

import type { BasecampTodo, BasecampTodoList } from '@/types/basecamp'

export type TodoListCounts = {
  open: number
  completed: number
  total: number
  /** Direct child nested lists (sections) of this list. */
  sectionCount: number
}

function childSections(
  list: BasecampTodoList,
  allLists: BasecampTodoList[],
): BasecampTodoList[] {
  const parentId = list.id?.toString()
  if (!parentId) return []
  return allLists.filter(
    (l) =>
      l.is_nested === true &&
      l.parent_id?.toString() === parentId,
  )
}

function accumulateTodos(todos: BasecampTodo[] | undefined): TodoListCounts {
  let open = 0
  let completed = 0
  for (const todo of todos || []) {
    if (todo.completed) completed += 1
    else open += 1
  }
  return { open, completed, total: open + completed, sectionCount: 0 }
}

/** Counts for one list including its nested sections' todos. */
export function countTodoListWithSections(
  list: BasecampTodoList,
  allLists: BasecampTodoList[],
): TodoListCounts {
  const sections = childSections(list, allLists)
  const own = accumulateTodos(list.todos)
  let open = own.open
  let completed = own.completed
  for (const section of sections) {
    const c = accumulateTodos(section.todos)
    open += c.open
    completed += c.completed
  }
  return {
    open,
    completed,
    total: open + completed,
    sectionCount: sections.length,
  }
}

/** Flat sample of open todos from the list + nested sections (for preview panes). */
export function sampleOpenTodosFromList(
  list: BasecampTodoList,
  allLists: BasecampTodoList[],
  limit: number,
): BasecampTodo[] {
  const sections = childSections(list, allLists)
  const out: BasecampTodo[] = []
  for (const todo of list.todos || []) {
    if (todo.completed) continue
    out.push(todo)
    if (out.length >= limit) return out
  }
  for (const section of sections) {
    for (const todo of section.todos || []) {
      if (todo.completed) continue
      out.push(todo)
      if (out.length >= limit) return out
    }
  }
  return out
}
